<template>
  <div class="wt2-sg wt2-notification-feedback">
    <component
      class="-item"
      v-for="(msg, index) in displayedMessages"
      :key="msg.guid"
      :index="index"
      :msg="msg"
      :is="msg.getComponent()"
      :otherHovered.sync="otherHovered"
    >
      <template slot="closeItemSlot" slot-scope="child">
        <div class="-close-item" @click="child.closeItem">
          <i class="wticon wticon-close"></i>
        </div>
      </template>
    </component>
  </div>
</template>

<script>
import app from 'app';
import CommonMessageInterface from 'v/notification/CommonMessageInterface.vue';
import { mapState } from 'vuex';
import audioNotification from 'c/audioNotification';
import MotoMaintenance from 'v/notification/MotoMaintenance.vue';
import MotoVersionUpdate from 'v/notification/MotoVersionUpdate.vue';

// language=Vue
const MotoTodo = CommonMessageInterface.extend({
  template: `
<div class="WARN">
    <div class="-info-box">// TODO: {{msg.event}}</div>
    <slot name="closeItemSlot" :closeItem="closeItem"/>
</div>
`.normalizeSpace(),
});

// language=Vue
const MotoManualNotification = CommonMessageInterface.extend({
  template: `
<div class="-moto-manual-notification"
    :class="messageType">
    <div class="-info-box">
        <div class="-message" v-html="msg.text"></div>
    </div>
    <slot name="closeItemSlot" :closeItem="closeItem"/>
</div>
`.normalizeSpace(),
});

// language=Vue
const MotoVideoPublished = CommonMessageInterface.extend({
  template: `
<div :class="messageType">
    <div class="-poster-box">
        <img :src="mixinSrc(msg.storyThumbnailUrl, defaultPoster)" alt="poster"/>
    </div>
    <div v-if="isError" class="-info-box">
        <div class="wt2-text-m2">Ouch… looks like a problem!</div>
        <div class="wt2-text-n -title-ellipsis">{{msg.storyTitle}}.</div>
        <div class="wt2-text-n -message">
            A technical issue is preventing video production and we’re currently working on it.
            We’ll notify you by email once it’s fixed. Thanks for your patience.
        </div>
    </div>
    <div v-else class="-info-box">
        <div class="wt2-text-m2">{{msg.storyboard ? 'Your Storyboard is ready' : 'Your video is ready'}}.</div>
        <div class="wt2-text-n -title-ellipsis">{{msg.storyTitle}}.</div>
        <div class="wt2-text-n">
            <a @click="openStory" style="cursor: pointer;">Click to watch</a>
        </div>
    </div>
    <slot name="closeItemSlot" :closeItem="closeItem"/>
</div>
`.normalizeSpace(),

  methods: {
    openStory(e) {
      this.closeItem(e);
      app.controller.openProducedStoryModal(this.msg.storyId);
    },
  },
});

// language=Vue
const MotoDraftReady = CommonMessageInterface.extend({
  template: `
<div :class="messageType">
    <div class="-poster-box">
        <img :src="mixinSrc(msg.storyThumbnailUrl, defaultPoster)" alt="poster"/>
    </div>
    <div v-if="isError" class="-info-box">
        <div class="wt2-text-m2">Ouch… looks like a problem!</div>
        <div class="wt2-text-n -title-ellipsis">{{msg.storyTitle}}.</div>
        <div class="wt2-text-n -message">
            A technical issue is preventing AI video production and we’re currently working on it.
            We’ll notify you by email once it’s fixed. Thanks for your patience.
        </div>
    </div>
    <div v-else class="-info-box">
        <div class="wt2-text-m2">{{'Your AI draft is ready'}}.</div>
        <div class="wt2-text-n -title-ellipsis">{{msg.storyTitle}}.</div>
        <div class="wt2-text-n">
            <a @click="navigateAiStory" style="cursor: pointer;">Click to edit</a>
        </div>
    </div>
    <slot name="closeItemSlot" :closeItem="closeItem"/>
</div>
`.normalizeSpace(),

  methods: {
    navigateAiStory(e) {
      this.closeItem();
      if (app.controller.isStoryPage()) {
        window.open('/stories/' + this.msg.storyId, '_blank');
      } else {
        app.navigate('/stories/' + this.msg.storyId);
      }
    },
  },
  computed: {},
});

// language=Vue
const MotoVoiceOverReady = CommonMessageInterface.extend({
  template: `
<div>
    <div class="-poster-box">
        <img :src="mixinSrc(msg.storyThumbnailUrl, defaultPoster)" alt="poster"/>
    </div>
    <div class="-info-box">
        <div class="wt2-text-m2">Your voice over is ready.</div>
        <div class="wt2-text-n -title-ellipsis">{{msg.storyTitle}}.</div>
        <div class="wt2-text-n">
            <a target="_blank"
                :href="'/stories/'+ msg.storyId"
                @click="closeItem"
            >Click to edit</a> video
        </div>
    </div>
    <slot name="closeItemSlot" :closeItem="closeItem"/>
</div>
`.normalizeSpace(),
});

// language=Vue
const MotoCopyLockedStory = CommonMessageInterface.extend({
  template: `
<div>
    <div class="-poster-box">
        <img :src="mixinSrc(msg.storyThumbnailUrl, defaultPoster)" alt="poster"/>
    </div>
    <div class="-info-box">
        <div class="wt2-text-m2">
        Your video has been copied by {{msg.lockedStoryUserName}} for further editing
        </div>
        <div class="wt2-text-n">
        Please consult with your colleague to avoid multiple versions of the same video.
        <br>
        {{msg.storyTitle}}</div>
    </div>
    <slot name="closeItemSlot" :closeItem="closeItem"/>
</div>
`.normalizeSpace(),
});

// language=Vue
const MotoSocialUploadVideoReady = CommonMessageInterface.extend({
  template: `
<div :class="messageType">
    <div class="-poster-box">
        <img :src="mixinSrc(msg.storyThumbnailUrl, defaultPoster)" alt="poster"/>
    </div>
    <div v-if="isError" class="-info-box">
        <div class="wt2-text-m2">Looks like uploading to
            <span class="text-capitalize">{{msg.socialProvider}}</span>
        has failed.</div>
        <div class="wt2-text-n">Try again.</div>
        <div class="wt2-text-n -title-ellipsis">{{msg.storyTitle}}.</div>
    </div>
    <div v-else class="-info-box">
        <div class="wt2-text-m2">Congrats! Your video is uploaded to
            <span class="text-capitalize">{{msg.socialProvider}}</span>.
        </div>
        <div class="wt2-text-n -title-ellipsis">{{msg.storyTitle}}.</div>
        <div class="wt2-text-n">
            <a target="_blank"
                :href="mixinSrc(msg.socialVideoUrl)"
                :title="msg.storyTitle"
                @click="closeItem"
            >Click to watch</a>
        </div>
    </div>
    <slot name="closeItemSlot" :closeItem="closeItem"/>
</div>
`.normalizeSpace(),
});

const MotoThumbnailMessage = CommonMessageInterface.extend({
  template: `
<div :class="messageType">
    <div v-if="msg.storyThumbnailUrl" class="-poster-box">
        <img :src="mixinSrc(msg.storyThumbnailUrl, defaultPoster)" alt="poster"/>
    </div>
    <div class="-info-box ml2">
        <div class="wt2-text-m2">{{msg.title}}</div>
        <div class="wt2-text-n -title-ellipsis">{{msg.description}}</div>
        <div class="wt2-text-n">{{msg.extra}}</div>
    </div>
    <slot name="closeItemSlot" :closeItem="closeItem"/>
</div>
`.normalizeSpace(),
});

const MotoMassProduce = CommonMessageInterface.extend({
  template: `
<div :class="messageType">
    <div v-if="isError" class="-info-box">
        <div class="wt2-text-m2">Ouch… looks like a problem!</div>
        <div class="wt2-text-n -message">
            Your video batch "{{msg.channelBatchId}}" had problems during production.
            Please try again or contact support.
        </div>
    </div>
    <div v-else class="-info-box">
        <div class="wt2-text-m2">Your video batch "{{msg.channelBatchId}}" is ready.</div>
        <div class="wt2-text-n">
            <a target="_blank"
                :href="'/dashboard/myVideos'"
                @click="closeItem"
            >Go to Produced Videos</a>
        </div>
    </div>
    <slot name="closeItemSlot" :closeItem="closeItem"/>
</div>
`.normalizeSpace(),
});

/**
 * @constructor
 */
function Message(msg) {
  /**
   * copying msg.data props into this instance
   * @note: moving message property into data object for convenience
   */
  Object.assign(this, msg.data, { message: msg.message });

  /**
   * Unique GUI ID
   * @type {String}
   */
  this.guid = app.guid(msg.event);

  /**
   * message private decay time
   * @type {Number}
   */
  if (msg.data.timeout) {
    this.decayTimeout = msg.data.timeout;
  } else {
    this.decayTimeout = app.TIME.second(5);
  }

  /**
   * Control message visibility
   * @type {boolean}
   */
  this.decayed = false;
}

Object.assign(Message, {
  eventMap: {
    MANUAL_NOTIFICATION: { component: 'moto-manual-notification' },
    VIDEO_PUBLISHED: { component: 'moto-video-published' },
    DRAFT_READY: { component: 'moto-draft-ready' },
    STORYBOARD: { component: 'moto-voice-over-ready' },
    COPY_LOCKED_STORY: { component: 'moto-copy-locked-story' },
    SOCIAL_UPLOAD_VIDEO_READY: { component: 'moto-social-upload-video-ready' },
    STORY_MESSAGE: { component: 'moto-thumbnail-message' },
    MASS_PRODUCE_BATCH_DONE: { component: 'moto-mass-produce' },
    LOCK_FOR_MAINTENANCE: { component: 'moto-maintenance' },
    VERSION_UPDATE: { component: 'moto-version-update' },
  },

  getSupportedEvents() {
    return Object.keys(Message.eventMap);
  },
});

Object.assign(Message.prototype, {
  getComponent() {
    // list of supported events and their components/templates/views
    return _.isObject(this.customComponent)
      ? this.customComponent
      : Message.eventMap[this.event].component;
  },
});

const storyRelevantNotificationGroupNames = [
  'BGMUSIC_EXPIRATION',
  'AUTOSAVE_ALERTS',
  'TIMELINE_TOO_LONG',
  'STORY_MIGRATED_TO_NEW_VO',
  'AUDIO_UPLOADED_FROM_GALLERY',
];

export default {
  name: 'NotificationFeedback',

  components: {
    MotoTodo,
    MotoManualNotification,
    MotoVideoPublished,
    MotoDraftReady,
    MotoVoiceOverReady,
    MotoSocialUploadVideoReady,
    MotoCopyLockedStory,
    MotoThumbnailMessage,
    MotoMassProduce,
    MotoMaintenance,
    MotoVersionUpdate,
  },

  data() {
    return {
      MAX_MESSAGES_TO_DISPLAY_SIMULTANEOUSLY: 8,
      messages: [],
      // synced hover state over one of the views
      otherHovered: false,
    };
  },

  computed: {
    ...mapState({
      routePath: (state) => state.routePath,
    }),

    displayedMessages() {
      // retrieve non-decayed then limit by max number
      return _.filter(
        _.filter(this.messages, (msg) => !msg.decayed),
        (msg, i) => i < this.MAX_MESSAGES_TO_DISPLAY_SIMULTANEOUSLY
      );
    },
  },

  watch: {
    routePath: '$_routeChanged_checkMessageRelevancy',
  },

  beforeDestroy() {
    app.notificationController.unsubscribe(this.$_subscribeHandler);
  },

  mounted() {
    app.notificationController.subscribe(this.$_subscribeHandler, {
      events: Message.getSupportedEvents(),
    });
  },

  methods: {
    /**
     * @param e {CustomEvent} from `app.notificationController`
     */
    $_subscribeHandler(e) {
      if (e.detail.data.uniqueGroupName) {
        this.messages
          .filter((m) => m.uniqueGroupName === e.detail.data.uniqueGroupName)
          .forEach((m) => (m.decayed = true));
      }
      if (e.detail.data.remove === true && e.detail.data.notificationId) {
        this.messages = this.messages.filter((msg) => {
          if (e.detail.data.notificationId === msg.notificationId) {
            msg.decayed = true;
          }
          return !msg.decayed;
        });
      } else {
        this.messages.unshift(new Message(e.detail));
      }
    },

    $_routeChanged_checkMessageRelevancy() {
      if (!/^stories\//.test(this.routePath)) {
        // filter-out story relevant messages when moving out of story context
        this.messages = this.messages.filter((msg) => {
          if (
            storyRelevantNotificationGroupNames.includes(msg.uniqueGroupName)
          ) {
            msg.decayed = true;
          }
          return !msg.decayed;
        });
      }
    },

    notifyBySound: _.throttle(function () {
      audioNotification.generic();
    }, app.TIME.second()),
  },
};
</script>
